﻿import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation
} from 'react-router-dom';
import React, { useContext, useEffect, useRef } from 'react';
import KnowledgeAssistantPage from './components/pages/KnowledgeAssistantPage';
import { ToastContainer } from 'react-toastify';
import Layout from './components/shared/Layout';
import HomePage from './components/pages/HomePage';
import BrowseTopicPage from './components/pages/BrowseTopicPage';
import ArticlePage from './components/pages/ArticlePage';
import ArticlesByTopicsPage from './components/pages/ArticlesByTopicsPage';
import AllArticlesPage from './components/pages/AllArticlesPage';
import NotFoundPage from './components/pages/NotFoundPage';
import useBotPagesConfig from './hooks/useBotPagesConfig';
import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate
} from '@azure/msal-react';
import SignIn from './components/shared/SignIn';
import {
    ThreadContext,
    ThreadProvider
} from './components/knowledge/ThreadContext.tsx';

const ClearThreadOnRouteChange: React.FC = () => {
    const location = useLocation();
    const { setThreadId } = useContext(ThreadContext);
    const prevPath = useRef(location.pathname);

    useEffect(() => {
        if (prevPath.current !== location.pathname) {
            // Route has changed, clear threadInfo
            setThreadId(null);
        }
        prevPath.current = location.pathname;
    }, [location, setThreadId]);

    return null;
};

const App: React.FC = () => {
    const { requiresAuthentication } = useBotPagesConfig();

    return (
        <ThreadProvider>
            <BrowserRouter>
                <ClearThreadOnRouteChange />
                <Layout>
                    <ToastContainer pauseOnFocusLoss={false} />
                    {requiresAuthentication ? (
                        <>
                            {/* When authentication is required */}
                            <AuthenticatedTemplate>
                                {/* Render authenticated routes */}
                                <Routes>
                                    <Route path='/' element={<HomePage />} />
                                    <Route
                                        path='/assistant'
                                        element={<KnowledgeAssistantPage />}
                                    />
                                    <Route
                                        path='/topic/:tagText'
                                        element={<BrowseTopicPage />}
                                    />
                                    <Route
                                        path='/article/*'
                                        element={<ArticlePage />}
                                    />
                                    <Route
                                        path='/related_articles'
                                        element={<ArticlesByTopicsPage />}
                                    />
                                    <Route
                                        path='/articles'
                                        element={<AllArticlesPage />}
                                    />
                                    <Route
                                        path='/auth'
                                        element={<Navigate to={'/'} replace />}
                                    />
                                    <Route
                                        path='*'
                                        element={<NotFoundPage />}
                                    />
                                </Routes>
                            </AuthenticatedTemplate>

                            <UnauthenticatedTemplate>
                                {/* Render SignIn component when not authenticated */}
                                <Routes>
                                    <Route path='*' element={<SignIn />} />
                                </Routes>
                            </UnauthenticatedTemplate>
                        </>
                    ) : (
                        <Routes>
                            <Route path='/' element={<HomePage />} />
                            <Route
                                path='/assistant'
                                element={<KnowledgeAssistantPage />}
                            />
                            <Route
                                path='/topic/:tagText'
                                element={<BrowseTopicPage />}
                            />
                            <Route
                                path='/article/*'
                                element={<ArticlePage />}
                            />
                            <Route
                                path='/related_articles'
                                element={<ArticlesByTopicsPage />}
                            />
                            <Route
                                path='/articles'
                                element={<AllArticlesPage />}
                            />
                            <Route path='*' element={<NotFoundPage />} />
                        </Routes>
                    )}
                </Layout>
            </BrowserRouter>
        </ThreadProvider>
    );
};

export default App;
